import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ConversationSummary,
  Project,
  deleteProject,
  deleteProjectFile,
  listProjectConversations,
  moveConversationToProject,
  updateProject,
  updateProjectMemory,
  uploadProjectFile,
} from '../services/api';
import { useProjects } from '../context/ProjectContext';
import { confirmDialog, notify, promptDialog } from './Notifications';
import {
  BrainIcon, ChatBubbleIcon, CloseIcon, FileTextIcon, FolderIcon, PencilIcon, SpinnerIcon, TrashIcon, UploadIcon,
} from './icons';
import Markdown from './ChatMessage/Markdown';

const PROJECT_FILE_ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.md,.json,.html,.htm,.rtf';

const formatSize = (bytes: number) =>
  bytes < 1024 ? `${bytes} B` : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const formatDate = (iso?: string) => {
  if (!iso) return '';
  const date = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);

  return isNaN(date.getTime()) ? '' : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const errorDetail = (err: any, fallback: string) => {
  const detail = err?.response?.data?.detail;

  return typeof detail === 'string' ? detail : detail?.message || fallback;
};

// ── Header: name, rename, delete ─────────────────────────────────────

export const ProjectHeader: React.FC<{ project: Project; onRenamed: (p: Project) => void; onDeleted: () => void }> = ({
  project, onRenamed, onDeleted,
}) => {
  const rename = async () => {
    const name = await promptDialog('Rename project', { defaultValue: project.name, confirmLabel: 'Rename' });
    if (!name || name === project.name) return;
    try {
      onRenamed(await updateProject(project.id, { name }));
    } catch (err) {
      notify(errorDetail(err, 'Could not rename the project.'));
    }
  };

  const remove = async () => {
    const confirmed = await confirmDialog(
      `Delete "${project.name}"? Its chats, files and memory are deleted for good.`,
      { confirmLabel: 'Delete project', danger: true },
    );
    if (!confirmed) return;
    try {
      await deleteProject(project.id);
      onDeleted();
    } catch (err) {
      notify(errorDetail(err, 'Could not delete the project.'));
    }
  };

  return (
    <div className="cv-project-header">
      <span className="cv-project-header__icon"><FolderIcon size={22} /></span>
      <h1 className="cv-project-header__name">{project.name}</h1>
      <button type="button" className="cv-icon-btn" onClick={rename} data-tooltip="Rename" aria-label="Rename project">
        <PencilIcon />
      </button>
      <button type="button" className="cv-icon-btn" onClick={remove} data-tooltip="Delete" aria-label="Delete project">
        <TrashIcon />
      </button>
    </div>
  );
};

// ── Memory: the markdown the model is given, shown and edited as such ─

const MemoryEditor: React.FC<{
  initial: string;
  maxChars: number;
  saving: boolean;
  onSave: (content: string) => void;
  onClose: () => void;
}> = ({ initial, maxChars, saving, onSave, onClose }) => {
  const [text, setText] = useState(initial);
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const tooLong = text.length > maxChars;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="cv-modal-backdrop" onClick={onClose}>
      <div
        className="cv-modal cv-memory-editor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cv-memory-editor-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cv-modal__header">
          <h2 id="cv-memory-editor-title" className="cv-modal__title">Project memory</h2>
          <div className="cv-memory-editor__tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'write'} onClick={() => setTab('write')}>Write</button>
            <button type="button" role="tab" aria-selected={tab === 'preview'} onClick={() => setTab('preview')}>Preview</button>
          </div>
        </div>
        <p className="cv-memory-editor__hint">
          Markdown, the same text the model is given at the start of every chat in this project.
          Use <code>## Section</code> headings and <code>- fact</code> lines.
        </p>
        {tab === 'write' ? (
          <textarea
            className="cv-memory-editor__input"
            aria-label="Project memory (markdown)"
            value={text}
            autoFocus
            spellCheck
            onChange={(e) => setText(e.target.value)}
            placeholder={'## Preferences\n- Answer with tables where possible'}
          />
        ) : (
          <div className="cv-memory-editor__preview">
            {text.trim() ? <Markdown>{text}</Markdown> : <p className="cv-project-panel__empty">Nothing to preview.</p>}
          </div>
        )}
        <div className="cv-confirm__actions cv-memory-editor__actions">
          <span className={`cv-memory-editor__count${tooLong ? ' is-over' : ''}`}>
            {text.length.toLocaleString()} / {maxChars.toLocaleString()}
          </span>
          <button type="button" className="cv-tool-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="cv-tool-btn cv-tool-btn--primary"
            disabled={saving || tooLong || text.trim() === initial.trim()}
            onClick={() => onSave(text)}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Matches MEMORY_MAX_CHARS on the backend, which rejects anything longer.
const MEMORY_MAX_CHARS = 8000;

const MemoryPanel: React.FC<{ project: Project; onChange: (p: Project) => void }> = ({ project, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const content = project.memory?.content || '';

  const save = async (next: string) => {
    setSaving(true);
    try {
      onChange(await updateProjectMemory(project.id, next, project.memory?.version ?? 0));
      setEditing(false);
    } catch (err: any) {
      const latest = err?.response?.status === 409 ? err.response.data?.detail?.project : null;
      if (latest) {
        // The extractor (or another tab) changed it first: show the latest rather than overwrite it.
        onChange(latest);
        setEditing(false);
        notify('Memory was updated in the meantime. This is the latest version; make your change again.', 'warning');
      } else {
        notify(errorDetail(err, 'Could not save the memory.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const toggle = async () => {
    if (project.memory_enabled) {
      const confirmed = await confirmDialog(
        'Turn off memory? Everything remembered in this project is wiped.',
        { confirmLabel: 'Turn off and wipe', danger: true },
      );
      if (!confirmed) return;
    }
    try {
      onChange(await updateProject(project.id, { memory_enabled: !project.memory_enabled }));
    } catch (err) {
      notify(errorDetail(err, 'Could not change the memory setting.'));
    }
  };

  return (
    <section className="cv-project-panel" aria-labelledby="cv-project-memory">
      <div className="cv-project-panel__head">
        <BrainIcon size={16} />
        <h2 id="cv-project-memory">Memory</h2>
        {project.memory_enabled && (
          <button type="button" className="cv-tool-btn" onClick={() => setEditing(true)}>
            <PencilIcon size={14} />
            Edit
          </button>
        )}
        <label className="cv-switch">
          <input type="checkbox" role="switch" checked={project.memory_enabled} onChange={toggle} aria-label="Project memory" />
          <span className="cv-switch__track" aria-hidden="true" />
        </label>
      </div>

      {!project.memory_enabled ? (
        <p className="cv-project-panel__empty">
          Off. Turn it on and Chat VNT remembers facts from this project's chats and uses them in every new chat here.
        </p>
      ) : content.trim() ? (
        <>
          <div className="cv-memory-view"><Markdown>{content}</Markdown></div>
          <div className="cv-project-panel__foot">
            {project.memory?.updated_at && <span>Updated {formatDate(project.memory.updated_at)}</span>}
            <button
              type="button"
              className="cv-tool-btn"
              onClick={async () => {
                if (await confirmDialog('Forget everything this project remembers?', { confirmLabel: 'Forget all', danger: true })) {
                  save('');
                }
              }}
            >
              Forget all
            </button>
          </div>
        </>
      ) : (
        <p className="cv-project-panel__empty">
          Nothing remembered yet. Facts appear here a few seconds after an answer in this project.
        </p>
      )}

      {editing && (
        <MemoryEditor
          initial={content}
          maxChars={MEMORY_MAX_CHARS}
          saving={saving}
          onSave={save}
          onClose={() => setEditing(false)}
        />
      )}
    </section>
  );
};

// ── Files ────────────────────────────────────────────────────────────

const FilesPanel: React.FC<{ project: Project; onChange: (p: Project) => void }> = ({ project, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      let latest = project;
      for (const file of Array.from(files)) {
        latest = await uploadProjectFile(project.id, file);
      }
      onChange(latest);
    } catch (err) {
      notify(errorDetail(err, 'Upload failed.'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (blobName: string, filename: string) => {
    if (!(await confirmDialog(`Remove ${filename} from the project?`, { confirmLabel: 'Remove', danger: true }))) return;
    try {
      await deleteProjectFile(project.id, blobName);
      onChange({ ...project, files: project.files.filter((f) => f.blob_name !== blobName) });
    } catch (err) {
      notify(errorDetail(err, 'Could not remove the file.'));
    }
  };

  return (
    <section className="cv-project-panel" aria-labelledby="cv-project-files">
      <div className="cv-project-panel__head">
        <FileTextIcon size={16} />
        <h2 id="cv-project-files">Files</h2>
        <span className="cv-badge">{project.files.length} / 5</span>
        <button type="button" className="cv-tool-btn" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading ? <SpinnerIcon size={14} /> : <UploadIcon size={14} />}
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        <input ref={inputRef} type="file" hidden multiple accept={PROJECT_FILE_ACCEPT} onChange={(e) => upload(e.target.files)} />
      </div>

      {project.files.length === 0 ? (
        <p className="cv-project-panel__empty">
          Documents here are available to every chat in the project. Up to 5 files, 50 MB in total.
        </p>
      ) : (
        <ul className="cv-project-list">
          {project.files.map((f) => (
            <li key={f.blob_name} className="cv-project-list__row">
              <FileTextIcon size={16} />
              <span className="cv-project-list__title">{f.filename}</span>
              <span className="cv-project-list__meta">{formatSize(f.size)}</span>
              <button
                type="button"
                className="cv-icon-btn"
                onClick={() => remove(f.blob_name, f.filename)}
                data-tooltip="Remove"
                aria-label={`Remove ${f.filename}`}
              >
                <TrashIcon size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {project.files.length > 0 && (
        <p className="cv-project-panel__hint">Uploading a file with the same name replaces it.</p>
      )}
    </section>
  );
};

// ── Chats ────────────────────────────────────────────────────────────

const ChatsPanel: React.FC<{ projectId: string; onOpen: (id: string) => void }> = ({ projectId, onOpen }) => {
  const [chats, setChats] = useState<ConversationSummary[] | null>(null);

  const load = useCallback(async () => {
    try {
      setChats(await listProjectConversations(projectId));
    } catch {
      setChats([]);
      notify('Could not load the chats in this project.');
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const removeFromProject = async (chat: ConversationSummary) => {
    try {
      await moveConversationToProject(chat.id, null);
      setChats((all) => (all || []).filter((c) => c.id !== chat.id));
    } catch (err) {
      notify(errorDetail(err, 'Could not move the chat.'));
    }
  };

  return (
    <section className="cv-project-panel cv-project-panel--wide" aria-labelledby="cv-project-chats">
      <div className="cv-project-panel__head">
        <ChatBubbleIcon size={16} />
        <h2 id="cv-project-chats">Chats</h2>
      </div>
      {chats === null ? (
        <p className="cv-project-panel__empty"><SpinnerIcon size={14} /> Loading…</p>
      ) : chats.length === 0 ? (
        <p className="cv-project-panel__empty">No chats yet. Start one above; it stays in this project.</p>
      ) : (
        <ul className="cv-project-list">
          {chats.map((chat) => (
            <li key={chat.id} className="cv-project-list__row cv-project-list__row--link">
              <button type="button" className="cv-project-list__open" onClick={() => onOpen(chat.id)}>
                <span className="cv-project-list__title">{chat.title}</span>
                <span className="cv-project-list__meta">{formatDate(chat.last_message_time)}</span>
              </button>
              <button
                type="button"
                className="cv-icon-btn"
                onClick={() => removeFromProject(chat)}
                data-tooltip="Remove from project"
                aria-label={`Remove ${chat.title} from the project`}
              >
                <CloseIcon size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

// ── Page body below the chat input ───────────────────────────────────

export const ProjectDetails: React.FC<{
  project: Project;
  onChange: (p: Project) => void;
  onOpenConversation: (id: string) => void;
  onRefresh?: () => void;
}> = ({ project, onChange, onOpenConversation, onRefresh }) => {
  // Memory is written a few seconds after each answer, so the copy held since the page was last
  // shown is stale: refetch on arrival and whenever the tab regains focus.
  useEffect(() => {
    onRefresh?.();
    const onFocus = () => onRefresh?.();
    window.addEventListener('focus', onFocus);

    return () => window.removeEventListener('focus', onFocus);
  }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="cv-project-details">
      <ChatsPanel projectId={project.id} onOpen={onOpenConversation} />
      <FilesPanel project={project} onChange={onChange} />
      <MemoryPanel project={project} onChange={onChange} />
    </div>
  );
};

// Keeps the context in step after any change made on the page.
export const useProjectPageActions = () => {
  const { activeProject, refreshProjects, refreshActiveProject, selectProject, replaceActiveProject } = useProjects();

  return {
    project: activeProject,
    onChange: replaceActiveProject,
    onRefresh: refreshActiveProject,
    onDeleted: async () => {
      await selectProject(null);
      await refreshProjects();
    },
  };
};
